// ====================== PageConfig.jsx (Table-Only Version with Floating Iframe) ======================
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    Snackbar,
} from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import figLogo from "../../assets/fig_CROP.png";

const PageConfig = () => {
    // Basic state variables
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [iframeOpen, setIframeOpen] = useState(false);

    // State for editing the ai_prompt_instructions field.
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(null);
    const [promptInstructions, setPromptInstructions] = useState("");
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);

    // New states for filtering by organization
    const [filterMode, setFilterMode] = useState("all"); // "all" or "organization"
    const [selectedOrganization, setSelectedOrganization] = useState("");

    // Fetch the pages from the API endpoint
    const fetchPages = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                "https://api.swancreate.com/api/llm/sail_ai_pages"
            );
            if (!response.ok) {
                throw new Error("Failed to fetch pages");
            }
            const data = await response.json();
            setPages(data);
        } catch (err) {
            console.error("Error fetching pages:", err.message);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPages();
    }, []);

    // Compute unique organization names (using OrganizationName if available, otherwise SchoolName)
    const uniqueOrganizations = Array.from(
        new Set(
            pages
                .map((page) => page.OrganizationName || page.SchoolName)
                .filter(Boolean)
        )
    );

    // Filter pages based on the filterMode and selected organization
    const filteredPages =
        filterMode === "all"
            ? pages
            : pages.filter((page) => {
                  const org = page.OrganizationName || page.SchoolName;
                  return org === selectedOrganization;
              });

    // Utility function to build the AI wrapper link (unchanged)
    const getAiLink = (page) =>
        `https://sailwingman.swancreate.com/ai-pagewrapper?url=${encodeURIComponent(
            page.PageUrl
        )}&apiid=${encodeURIComponent(page.apiid)}`;

    // Opens the AI page in a new window when the action button is clicked
    const handleOpenAiPage = (page) => {
        window.open(getAiLink(page), "_blank");
    };

    // Handler for opening the Update Training dialog
    const handleEditPrompt = (page) => {
        setCurrentPage(page);
        setPromptInstructions(page.ai_prompt_instructions || "");
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setCurrentPage(null);
        setPromptInstructions("");
    };

    // Handler to save the updated prompt instructions
    const handleSavePrompt = async () => {
        if (!currentPage) return;
        try {
            const response = await fetch(
                `https://api.swancreate.com/api/llm/sail_ai_pages/${currentPage.apiid}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ ai_prompt_instructions: promptInstructions }),
                }
            );
            if (!response.ok) {
                throw new Error("Failed to update prompt instructions");
            }
            // Optionally, refresh the page list
            await fetchPages();
            setSnackbarMessage("Prompt instructions updated successfully.");
            setShowSnackbar(true);
            setEditDialogOpen(false);
            setCurrentPage(null);
            setPromptInstructions("");
        } catch (err) {
            console.error("Error updating prompt instructions:", err.message);
            setSnackbarMessage("Failed to update prompt instructions.");
            setShowSnackbar(true);
        }
    };

    // DataGrid columns definition with the new Organization Name column
    const columns = [
        {
            field: "PageTitle",
            headerName: "Page Title",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Typography sx={{ color: "#333" }}>{params.value}</Typography>
            ),
        },
        {
            // Display OrganizationName if available; otherwise fall back to SchoolName
            field: "OrganizationName",
            headerName: "Organization Name",
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Typography sx={{ color: "#333" }}>
                    {params.value || params.row.SchoolName || "-"}
                </Typography>
            ),
        },
        {
            field: "PageDescription",
            headerName: "Description",
            flex: 2,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ color: "#333" }}>
                    {params.value ?? ""}
                </Typography>
            ),
        },
        {
            field: "aiLink",
            headerName: "AI Link",
            flex: 2,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                const link = getAiLink(row);
                return (
                    <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: "#1976d2",
                            textDecoration: "underline",
                            wordBreak: "break-all",
                        }}
                    >
                        {link}
                    </a>
                );
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                return (
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            fontWeight: "bold",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "#1565c0",
                            },
                            fontSize: "0.9rem",
                        }}
                        onClick={() => handleOpenAiPage(row)}
                    >
                        Open AI Page
                    </Button>
                );
            },
        },
        {
            field: "editPrompt",
            headerName: "Update Training",
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                return (
                    <Button
                        variant="outlined"
                        sx={{
                            borderColor: "#ff9800",
                            color: "#ff9800",
                            fontWeight: "bold",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "rgba(255, 152, 0, 0.08)",
                            },
                            fontSize: "0.9rem",
                        }}
                        onClick={() => handleEditPrompt(row)}
                    >
                        Update Training
                    </Button>
                );
            },
        },
    ];

    // Handlers for opening/closing the floating iframe
    const handleOpenIframe = () => {
        setIframeOpen(true);
    };

    const handleCloseIframe = () => {
        setIframeOpen(false);
    };

    // Handler for closing the Snackbar
    const handleCloseSnackbar = () => {
        setShowSnackbar(false);
        setSnackbarMessage("");
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                backgroundColor: "#ecf7fe",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: { xs: 2, md: 4 },
            }}
        >
            {/* FIG Logo at the top center */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <img src={figLogo} alt="FIG Logo" style={{ width: "140px" }} />
            </Box>

            {/* Title and Description */}
            <Typography
                variant="h4"
                gutterBottom
                sx={{
                    color: "#333",
                    fontWeight: "bold",
                    mb: 1,
                    textAlign: "center",
                }}
            >
                Page Configuration
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: "#333",
                    mb: 3,
                    maxWidth: "800px",
                    textAlign: "center",
                    fontSize: "1.1rem",
                }}
            >
                Manage your configured pages here.
            </Typography>

            {/* Error Alert and Loading Spinner */}
            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 2,
                        maxWidth: "600px",
                        width: "100%",
                    }}
                >
                    {error}
                </Alert>
            )}
            {loading && <CircularProgress sx={{ color: "#1976d2" }} />}

            {/* TOP CONTROLS: Create New Page Button and Filter Options */}
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "1600px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        fontSize: "1rem",
                        fontWeight: "bold",
                        textTransform: "none",
                        "&:hover": {
                            backgroundColor: "#1565c0",
                        },
                    }}
                    onClick={handleOpenIframe}
                >
                    Create New Page
                </Button>

                {/* Filter toggle: All Pages or By Organization */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ToggleButtonGroup
                        value={filterMode}
                        exclusive
                        onChange={(event, newMode) => {
                            if (newMode !== null) {
                                setFilterMode(newMode);
                                // Reset selected organization when switching modes
                                if (newMode === "all") {
                                    setSelectedOrganization("");
                                }
                            }
                        }}
                    >
                        <ToggleButton value="all">All Pages</ToggleButton>
                        <ToggleButton value="organization">By Organization</ToggleButton>
                    </ToggleButtonGroup>
                    {filterMode === "organization" && (
                        <TextField
                            select
                            label="Organization"
                            value={selectedOrganization}
                            onChange={(e) => setSelectedOrganization(e.target.value)}
                            SelectProps={{ native: true }}
                            variant="outlined"
                            sx={{ ml: 2, width: 200 }}
                        >
                            <option value="">Select Organization</option>
                            {uniqueOrganizations.map((org, index) => (
                                <option key={index} value={org}>
                                    {org}
                                </option>
                            ))}
                        </TextField>
                    )}
                </Box>
            </Box>

            {/* DATA GRID of existing pages */}
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "1600px",
                    height: 800,
                    mb: 4,
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    border: "1px solid #ddd",
                    overflow: "hidden",
                }}
            >
                <DataGrid
                    rows={filteredPages}
                    columns={columns}
                    getRowId={(row) => row.apiid}
                    pageSize={50}
                    rowsPerPageOptions={[50, 100, 250]}
                    disableSelectionOnClick
                    sx={{
                        border: "none",
                        color: "#333",
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#f5f5f5",
                            color: "#333",
                            fontWeight: "600",
                            borderBottom: "1px solid #ddd",
                        },
                        "& .MuiDataGrid-footerContainer": {
                            backgroundColor: "#f5f5f5",
                            color: "#333",
                            borderTop: "1px solid #ddd",
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid #eee",
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "#f9f9f9",
                        },
                    }}
                />
            </Box>

            {/* Floating Iframe Dialog for the Key Setup Page */}
            <Dialog open={iframeOpen} onClose={handleCloseIframe} maxWidth="lg" fullWidth>
                <DialogTitle
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", color: "#333" }}
                >
                    API Key Setup
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseIframe}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "#666",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <iframe
                        src="https://swanoffice.us:8443/lfserver/sail_ai_keysetup"
                        title="API Key Setup"
                        style={{ width: "100%", height: "600px", border: "none" }}
                    />
                </DialogContent>
            </Dialog>

            {/* Dialog for Editing the ai_prompt_instructions */}
            <Dialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", color: "#333" }}
                >
                    Edit AI Prompt Instructions
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseEditDialog}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "#666",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{ height: 500, overflowY: "auto", backgroundColor: "#fff" }}
                >
                    <TextField
                        value={promptInstructions}
                        onChange={(e) => setPromptInstructions(e.target.value)}
                        multiline
                        fullWidth
                        variant="outlined"
                        sx={{ minHeight: "100%", mb: 2 }}
                    />
                </DialogContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        p: 2,
                        backgroundColor: "#f5f5f5",
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleSavePrompt}
                        sx={{
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#1565c0" },
                            mr: 1,
                        }}
                    >
                        Save
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleCloseEditDialog}
                        sx={{
                            color: "#333",
                            borderColor: "#999",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#eee" },
                        }}
                    >
                        Cancel
                    </Button>
                </Box>
            </Dialog>

            {/* Snackbar for messages */}
            <Snackbar
                open={showSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="info"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PageConfig;
